import moment from 'moment-timezone';

import { GamePlayerStatus } from '../../../Constants';


// Wire-data key carrying the last-seen GamePlayer.last_action_uuid on
// every outgoing Click. Under CC_CLICK_NONCE_PROTOCOL the backend
// (engine/clicks/click_router.py) treats a stale parent_action_uuid as
// telemetry only — it logs the mismatch but does NOT reject. Mirrors the
// Python constant of the same name in click_router.py.
export const PARENT_ACTION_UUID = 'parent_action_uuid';

// CC_CLICK_NONCE_PROTOCOL. Wire-data key carrying a fresh
// client-generated nonce on every outgoing Click. The backend rejects
// any click_uuid it has already processed for that seat (true replays
// and double-deliveries) — duplicate detection is server-authoritative
// and requires no client pacing. A click that arrives with no click_uuid
// is rejected outright (no bypass). Mirrors the Python constant of the
// same name in click_router.py.
export const CLICK_UUID = 'click_uuid';


// Generate a fresh click nonce. Prefers crypto.randomUUID (all current
// browsers, and the Discord Activity target); falls back to an
// RFC4122-shaped v4 string for any runtime that lacks it (older browsers,
// some test environments). A click nonce needs uniqueness, not
// cryptographic strength, so the fallback's Math.random source is fine.
function generateClickUuid() {
    if (typeof crypto !== 'undefined'
            && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const rand = Math.floor(Math.random() * 16);
        const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
        return value.toString(16);
    });
}


// 2026-05-16: Mirror of engine/clicks/click_router.py CLICK_ROUTES.
// Any Click package sent while the seat's status isn't in this set
// gets a backend "Invalid Click Message ... :Waiting :Off :None ..."
// log line and is dropped server-side. The frontend AI fires stale
// clicks all the time — usually because the seat flipped to WAITING
// between the AI deciding and the package leaving. Drop them on this
// side so the console isn't flooded with rejections.
const STATUSES_THAT_ACCEPT_CLICKS = new Set([
    GamePlayerStatus.PICK_DRAFT_ID,
    GamePlayerStatus.GHOSTED_ID,
    // 2026-05-16 (afternoon): Mirror the GHOST_STARTER_ID safety net
    // that click_router.py added the same day. The backend now
    // routes GHOST_STARTER_ID clicks through message_while_ghosted
    // and the AI STATUS_ROUTES maps GHOST_STARTER_ID →
    // artificialPlaceGhost. Without this entry the allow-list dropped
    // the AI's placeGhost click before it left the browser, leaving
    // the seat stuck at GHOST_STARTER_ID with attacking_ghosts > 0.
    GamePlayerStatus.GHOST_STARTER_ID,
    GamePlayerStatus.DEFENDING_ID,
    GamePlayerStatus.TAKING_TURN_ID,
    GamePlayerStatus.SPECIAL_ID,
    GamePlayerStatus.SCORING_ID,
    GamePlayerStatus.RESEARCHING_ID,
    GamePlayerStatus.LODGING_ID,
    GamePlayerStatus.ROBOTING_ID,
    GamePlayerStatus.RENOVATING_ID,
    GamePlayerStatus.ADVERTISING_ID,
    GamePlayerStatus.GHOSTING_ID,
    GamePlayerStatus.RANDOM_ID,
    GamePlayerStatus.GAME_SETUP_ID,
    // Manual 3.1 opening ghost placement: per-click placeGhost while
    // the seat is in OPENING_GHOST_PLACEMENT_ID. Engine routes these
    // through click_router → opening_ghost_placement_click.
    GamePlayerStatus.OPENING_GHOST_PLACEMENT_ID,
    // Manual §3 in-game faction ceremony: commitFactionChoice while
    // the seat is in CHOOSE_FACTION_ID. Engine routes these through
    // click_router → choose_faction_response.
    GamePlayerStatus.CHOOSE_FACTION_ID,
]);


// CC_LODGING_ADD_DEDUP_STALL (prod G166, 2026-07-15): the seat's decision
// lifecycle — the signature whose change marks a genuinely NEW decision, so an
// identical payload is legitimate again. The single source of truth for both
// the send-seam duplicate latch (sendPackage, below) and the latch CLEAR in
// GamePlayerController.updateGamePlayerData, which imports this. Keep it that
// way: the two used to be hand-copied mirrors of each other.
//
// Why the board fields (lodging / docks / station) and not just the status
// triple: the triple alone assumed "a GENUINE next step of a multi-step action
// produces a DIFFERENT payload". That is false for every loop the engine runs
// at a FROZEN action_status —
//
//   * Lodging multi-add (G166, the stall this came from). The engine parks the
//     seat at ADD_ID for the WHOLE loop and every add click there is
//     {sold_robots: [], player_target: <self>, faction: X} — clockedOutStaff is
//     only ever set on the WAIT_FOR_JUICE pass — so `faction` is the ONLY
//     varying field. Two consecutive adds of the same faction are byte-
//     identical at a frozen triple, and the send-seam drop ate the second.
//   * Ghost placement / deghost, which repeat a dock (G829's two received
//     ghosts weighting to the SAME dock is an identical payload by design).
//   * Renovation / research loops, which repeat a station or storage index.
//
// A dropped click never reaches the server, so no patch and no Reject come
// back. A human seat on AUTO has neither the Feai reject-retry arm nor a hand
// on the mouse, so the drop is the same DEAD END that
// CC_AUTOPLAY_DEDUP_SILENT_DROP removed for bots — the seat strands forever
// (G166: the engine's last line is "Player 3 admin Execute Lodging" at
// 13:43:46, then silence; the seat never moved again).
//
// These board fields are the engine's ACK that the previous click landed: when
// one moves, the next identical-looking click is a NEW instruction, not a
// re-fire. They are exactly the seat's own board state out of
// ArtificialPlayer.getStatus() — the tracked set that can re-drive the seat at
// all — MINUS robot_assignments, which is deliberately EXCLUDED: it shuffles
// WITHOUT acking any Click (Robot packages are not even type 'Click'), and a
// stale Click re-fired during a robot shuffle is precisely the G65/G66
// double-fire this drop exists to contain.
export function decisionLifecycle(data) {
    return `${data.status}|${data.turn_status}|${data.action_status}`
        + `|${data.lodging}|${data.docks}|${data.station}`;
}


export default class GamePlayerPackageSender {

    constructor(gamePlayerAPI) {
        this.gamePlayerAPI = gamePlayerAPI;
        this.gamePlayerController = gamePlayerAPI.gamePlayerController;
        this.game = gamePlayerAPI.gamePlayerController.game;

        this.userId = this.game.userId;

        this.sendPackagedMessage = this.gamePlayerController.game.app.state.authenticator.sendPackagedMessage;

        this.packageId = 0;
    }

    sendPackage = (typeName, packageValue) => {
        if (typeName === 'Click' && !this._statusAcceptsClicks()) {
            return;
        }
        if (typeName === 'Click') {
            // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: drop a click that is
            // byte-identical to the one just sent for this seat WHILE the
            // seat's decision lifecycle (status/turn_status/action_status)
            // has not advanced. The engaged AI advisor re-enters its update
            // loop on every intermediate per-seat patch the engine streams
            // during ONE action (actionStatus INIT->WAIT, robot_assignments
            // shuffles), and at a fast speed step each re-entry drives another
            // concur -> another click built off a stale chain head that the
            // engine rejects (G65 Research double-fire, G66 stale ActionGrid,
            // 2026-07-08). The AI routes are deterministic on gamePlayerData,
            // so a GENUINE next step of a multi-step action (place the next
            // ghost, assign the next robot) produces a DIFFERENT payload and
            // is never suppressed; only a re-fire of the exact same decision —
            // a true duplicate — is dropped. The lifecycle guard means a
            // legitimate later repeat (after the engine advances the seat)
            // still goes through. A reject for this seat clears the latch (see
            // ArtificialPlayer.rejectEvent) so the reject-retry re-send is
            // never mistaken for a duplicate. Fingerprint BEFORE _stampAction-
            // Chain adds the per-click uuids (which are unique by design).
            //
            // CC_AUTOPLAY_DEDUP_SILENT_DROP (2026-07-09): the drop above is a
            // DEAD END — a dropped click never reaches the server, so no
            // GamePlayer patch and no Reject come back, and the reject-retry
            // one-shot (ArtificialPlayer.rejectEvent -> retryArmed) is never
            // armed. On a Feai BOT seat (artificial_player > 2) there is no
            // human and no manual UI to recover, so a FALSE-positive drop
            // strands the seat permanently — the deterministic AI legitimately
            // re-emits an identical payload at an UNCHANGED decision lifecycle
            // (the residual hole REVISION 2 of CC_ENGAGED_ADVISOR_SINGLE_EXECUTE
            // admitted; the send-seam cannot tell that legit re-emit from a
            // stale duplicate, and only the engine — which sees game state —
            // can). The recovery guarantee this restores: a Feai bot's click is
            // NEVER silently dropped. It always reaches the engine, which owns
            // duplicate detection WITH recovery — the click_uuid nonce catches
            // true replays/double-deliveries and typed Rejects
            // (stale_target_commit_...) catch stale state-based duplicates, and
            // a Reject re-fires the seat via CC_FEAI_REJECT_RETRY. One threat
            // per layer (HSH_STYLE §Protocol Changes): the bot path must not
            // re-buy the engine's dedup with an unrecoverable dead end. The drop
            // stays scoped to human-driven seats (artificial_player null/<=2)
            // — the engaged-advisor double-fire it was built for only occurs on
            // the owner's OWN human seat (SpeedController engaged), where the
            // human seat's manual controls are always a recovery path.
            const isFeaiBot =
                this.gamePlayerController.gamePlayerData.artificial_player > 2;
            const fingerprint = this._clickFingerprint(packageValue);
            const lifecycle = this._decisionLifecycle();
            const isDuplicate =
                this.gamePlayerController._lastClickFingerprint === fingerprint
                && this.gamePlayerController._lastClickLifecycle === lifecycle;
            if (!isFeaiBot) {
                if (isDuplicate) {
                    return;
                }
            } else if (this.gamePlayerController._feaiFallbackDrive
                    && isDuplicate) {
                // CC_FEAI_MIDACTION_REDRIVE_THRASH (prod G150, 2026-07-14): a
                // Feai bot's clicks otherwise ALWAYS reach the engine — the
                // recovery guarantee CC_AUTOPLAY_DEDUP_SILENT_DROP restored, so
                // the send seam never strands a bot by swallowing a legit
                // re-emit. The ONE exception is an empowered no-progress
                // fallback drive (ArtificialPlayer.updateEvent's
                // empoweredCurrentDrive): it re-runs the whole deterministic
                // decision tree against a LAGGED snapshot and re-emits a
                // byte-identical click the engine has already advanced past — a
                // bare player_target Lodging commit that lands at WAIT_FOR_JUICE
                // (stale_target_commit_at_wait_for_juice), or a
                // selectActionStack re-selection that lands with
                // available_actions already empty (available_actions_stack_
                // empty). Every one is Rejected, painting the RED action-window
                // UI and reject-storming the game (every seat of G150). Dropping
                // it HERE is safe and does NOT re-buy the engine's dedup with a
                // dead end, because:
                //   * only the fallback path is deduped — a normal diff-gate
                //     drive, a reject-retry, and a reconnect drive still ALWAYS
                //     reach the engine (the bot's genuine move is never dropped);
                //   * a Reject clears _lastClickFingerprint (ArtificialPlayer.
                //     rejectEvent), so a genuine reject-retry re-emit never
                //     matches here — the recovery path is never suppressed;
                //   * the fallback's LEGITIMATE uses (the G75 post-Execute
                //     continuation, the G121 Robot-Station owed follow-up)
                //     produce a DIFFERENT payload -> different fingerprint ->
                //     not dropped;
                //   * only a byte-identical click at an UNCHANGED decision
                //     lifecycle is dropped, which for a deterministic bot on the
                //     fallback path is always a stale duplicate the engine would
                //     Reject anyway;
                //   * the no-progress counter (consecutiveEmpoweredNoProgress
                //     Drives) still increments and belays after the cap, so a
                //     pathological loop surfaces manual controls rather than
                //     spinning silently.
                return;
            }
            this.gamePlayerController._lastClickFingerprint = fingerprint;
            this.gamePlayerController._lastClickLifecycle = lifecycle;
            this._stampActionChain(packageValue);
        }
        const message = this._makePackage(typeName, packageValue);
        this.sendPackagedMessage(message);
    };

    // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: a stable identity for the click's
    // meaning — its type + data, taken BEFORE the per-click uuids are stamped
    // on (those are unique on every emit and would defeat the compare). Two
    // clicks with the same fingerprint carry the same instruction.
    _clickFingerprint = (packageValue) => {
        return JSON.stringify([packageValue.type, packageValue.data || null]);
    };

    // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: the seat's decision lifecycle. While
    // this signature is unchanged the seat is on the same decision; once the
    // engine advances it the seat is on a new decision and an identical
    // payload is legitimate again.
    //
    // CC_LODGING_ADD_DEDUP_STALL (prod G166, 2026-07-15): the seat's BOARD
    // PROGRESS — lodging / docks / station — joins the status triple. The triple
    // alone assumed "a GENUINE next step of a multi-step action produces a
    // DIFFERENT payload". That is false for every loop the engine runs at a
    // FROZEN action_status:
    //
    //   * Lodging multi-add (G166, the stall this came from). The engine parks
    //     the seat at ADD_ID for the WHOLE loop and every add click there is
    //     {sold_robots: [], player_target: <self>, faction: X} — clockedOutStaff
    //     is only ever set on the WAIT_FOR_JUICE pass — so `faction` is the ONLY
    //     varying field. Two consecutive adds of the same faction are
    //     byte-identical at a frozen triple, and the drop above ate the second.
    //   * Ghost placement / deghost, which repeat a dock (G829's two received
    //     ghosts weighting to the SAME dock is an identical payload by design).
    //   * Renovation / research loops, which repeat a station or storage index.
    //
    // A dropped click never reaches the server, so no patch and no Reject come
    // back. A human seat on AUTO has neither the Feai reject-retry arm nor a
    // hand on the mouse, so the drop is the same DEAD END that
    // CC_AUTOPLAY_DEDUP_SILENT_DROP removed for bots — the seat strands
    // forever (G166: the engine's last line is "Player 3 admin Execute Lodging"
    // at 13:43:46, then silence).
    //
    // These three fields are the engine's ACK that the previous click landed:
    // when one moves, the next identical-looking click is a NEW instruction,
    // not a re-fire. They are exactly the seat's own board state out of
    // ArtificialPlayer.getStatus() — the tracked set that can re-drive the seat
    // at all — MINUS robot_assignments, which is deliberately excluded: it
    // shuffles WITHOUT acking any Click (Robot packages are not even type
    // 'Click'), and a stale Click re-fired during a robot shuffle is precisely
    // the G65/G66 double-fire this drop exists to contain.
    // The ONE definition lives at module scope (decisionLifecycle) and
    // GamePlayerController's latch-clear imports it. It used to keep its own
    // hand-copied mirror of the triple; two copies of one rule is how the
    // sender's key and the clear that unlocks it drift apart, and a drifted
    // clear is a stall.
    _decisionLifecycle = () => {
        return decisionLifecycle(this.gamePlayerController.gamePlayerData);
    };

    _stampActionChain = (packageValue) => {
        // Stamp both click-protocol fields on every outgoing Click
        // (CC_CLICK_NONCE_PROTOCOL).
        //
        // click_uuid: a fresh per-click nonce the server dedupes on —
        // catches replays and double-deliveries regardless of timing.
        //
        // parent_action_uuid: the last-seen action-chain head. Null until
        // the first GamePlayer patch arrives. The backend treats a stale
        // head as telemetry (logged, not rejected) — the pipelined FE
        // fires continuations off a lagging head by design.
        if (!packageValue.data) {
            packageValue.data = {};
        }
        const lastActionUuid =
            this.gamePlayerController.lastActionUuid;
        packageValue.data[PARENT_ACTION_UUID] =
            lastActionUuid !== undefined ? lastActionUuid : null;
        packageValue.data[CLICK_UUID] = generateClickUuid();
    };

    _statusAcceptsClicks = () => {
        const status =
            this.gamePlayerController.gamePlayerData.status;
        return STATUSES_THAT_ACCEPT_CLICKS.has(status);
    };

    _makePackage = (typeName, packageValue) => {
        ++this.packageId;
        let message = {};
        message.datetime = moment();
        message.user = this.userId;
        message.game = this.game.gameData.id;
        message.game_player = this.gamePlayerController.gamePlayerData.id;

        message.type = typeName;
        message.package = packageValue;

        return JSON.stringify(message);
    };
}
